/**
 * lib/supabase.ts
 * Supabase client configuration
 *
 * Provides centralized Supabase client setup with proper error handling
 * and environment configuration following coding standards.
 *
 * Architecture: WU1.5 - Uses generated types from supabase.types.ts
 */

import { createClient } from '@supabase/supabase-js';
import type { Database } from '@/types/supabase.types';

// Environment variables validation
const supabaseUrl = import.meta.env['VITE_SUPABASE_URL'];
const supabaseAnonKey = import.meta.env['VITE_SUPABASE_ANON_KEY'];

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables. Please check .env.local configuration.');
}

// TODO: [CONTRACTOR] Configure production environment setup - staging/production configs, service role keys, connection pooling

/**
 * Supabase client instance
 *
 * Configured with:
 * - Auto-refresh for long-running sessions
 * - Persistent sessions across browser refreshes
 * - Error handling for network issues
 * - Type-safe queries using generated Database schema
 *
 * TODO: [CONTRACTOR] Implement Row Level Security (RLS) policies - user data isolation, admin access controls, beta application permissions
 * TODO: [CONTRACTOR] Configure production authentication integration - JWT validation, session management, user context injection
 * TODO: [CONTRACTOR] Add real-time subscriptions for admin updates
 */
export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: false
  },
  realtime: {
    params: {
      eventsPerSecond: 10
    }
  }
});

// TODO: [CONTRACTOR] Implement service role configuration - server-side client with elevated permissions for admin operations, email sending, and bulk data processing

/**
 * Database health check utility
 *
 * Verifies Supabase connection and returns basic stats.
 * Useful for debugging and system status displays.
 */
export const getSupabaseHealth = async (): Promise<{ status: string; connected: boolean; applicationsCount?: number; error?: string }> => {
  try {
    // Simple query to test connection - use proper count syntax
    const { count, error } = await supabase
      .from('beta_applications')
      .select('*', { count: 'exact', head: true });

    if (error) {
      throw error;
    }

    return {
      status: 'healthy',
      connected: true,
      applicationsCount: count || 0
    };
  } catch (error) {
    console.error('Supabase health check failed:', error);
    return {
      status: 'error',
      connected: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
};

// TODO: [CONTRACTOR] Implement performance monitoring and optimization - connection pooling, query performance tracking, error monitoring, and alerting systems

/**
 * Export typed client for use throughout the application
 *
 * IMPORTANT: Repositories should import this client and use toDomain()/toWire()
 * from supabaseMappers.ts to convert between snake_case (wire) and camelCase (domain).
 */
export type SupabaseClient = typeof supabase;

/**
 * Re-export Database type for convenience
 */
export type { Database } from '@/types/supabase.types';

// TODO: [CONTRACTOR] Implement backup and disaster recovery strategy - automated backups, point-in-time recovery, cross-region replication, and data protection policies
